/**
 * Canonical app definitions index.
 *
 * This file is the single place the icon system imports app definitions from.
 * It is eagerly bundled and must stay side-effect free — all definition.ts
 * files imported here must not perform renderer registration or plugin
 * initialisation at module load time.
 *
 * Source-of-truth rules:
 *   - System apps  → icon lives in the definition.ts file below
 *   - Dynamic apps → icon lives in the DB (registered_apps) and is served via
 *                    GET /api/apps/installed; those are not listed here
 *
 * To change a system app icon, edit the matching definition.ts and redeploy.
 * Admin-panel icon editing applies only to dynamic apps.
 */

import type { AppDefinition } from '../types/apps';
import { canUseExperimentalFeatures } from '../stores/userStore';

// ── Core productivity ──────────────────────────────────────────────────────
import { DocsAppDefinition } from './DocsApp/definition';
import { TasksAppDefinition } from './TasksApp/definition';
import { NotesAppV2Definition } from './NotesApp_v2/definition';
import { NotesMarkdownAppDefinition } from './NotesMarkdownApp/definition';
import { MarkdownAppV2Definition } from './MarkdownApp_v2/definition';
import { SheetsAppV2Definition } from './SheetsApp_v2/definition';
import { PDFiumAppDefinition } from './PDFiumApp/definition';
import { PasswordVaultAppDefinition } from './PasswordVaultApp/definition';
import { BankAppDefinition } from './BankApp/definition';
import { BookkeeperAppDefinition } from './BookkeeperApp/definition';
import { PageAppV2Definition } from './PageApp_v2/definition';

// ── Communication ──────────────────────────────────────────────────────────
import { ChatAppDefinition } from './ChatApp/definition';
import { MailAppDefinition } from './MailApp/definition';
import { MailOverviewAppDefinition } from './MailApp/mailOverviewDefinition';
import { NotificationsAppDefinition } from './NotificationsApp/definition';
import { InboxAppDefinition } from './InboxApp/definition';
import { PromptsAppV2Definition } from './PromptsApp_v2/definition';
import { PromptLibraryAppDefinition } from './PromptLibraryApp/definition';

// ── Browsing & files ───────────────────────────────────────────────────────
import { BrowserAppDefinition } from './BrowserApp/definition';
import { RiverDriveAppDefinition } from './RiverDriveApp/definition';
import { DriveAppDefinition } from './DriveApp/definition';
import { GoogleDriveFileAppDefinition } from './GoogleDriveFileApp/definition';
import { LocalFileAppDefinition } from './LocalFileApp/definition';
import FileViewerAppDefinition from './FileViewerApp/definition';
import FileEditorAppDefinition from './FileEditorApp/definition';
import { ImageStudioAppDefinition } from './ImageStudioApp/definition';
import { MediaViewerAppDefinition } from './ImageStudioApp/MediaViewerApp.definition';
import { WebDocumentAppDefinition } from './WebDocumentApp/definition';

// ── Content creation ───────────────────────────────────────────────────────
import { CanvasAppDefinition } from './CanvasApp/definition';
import { DiagramAppDefinition } from './DiagramApp/definition';
import { ChartAppDefinition } from './ChartApp/definition';
import { SchemaEntityAppDefinition } from './SchemaEntityApp/definition';
import { VideoStudioAppDefinition } from './VideoStudioApp/definition';
import { MediaGenAppDefinition } from './MediaGenApp/definition';
import { NeurvanaAppDefinition } from './NeurvanaApp/definition';
import { WritersDashboardAppDefinition } from './WritersDashboardApp_v2/definition';
import { BookFormatterAppDefinition } from './BookFormatterApp_v2/definition';
import { ManuscriptReviewReportAppDefinition } from './ManuscriptReviewReportApp_v2/definition';
import { ListenAppDefinition } from './ListenApp/definition';

// ── AI & agents ────────────────────────────────────────────────────────────
import { AutomationsAppDefinition } from './AutomationsApp/definition';
import { MemoryAppV2Definition } from './MemoryApp_v2/definition';
import { SkillsAppV2Definition } from './SkillsApp_v2/definition';
import { SkillWizardAppDefinition } from './SkillWizardApp/definition';
import { transcriptAppDefinition } from './TranscriptApp/definition';
import { EntityGraphAppDefinition } from './EntityGraphApp/definition';
import { DatabaseAppDefinition } from './DatabaseApp/definition';

// ── Organisation & system ──────────────────────────────────────────────────
import { StoreAppDefinition } from './StoreApp/definition';
import { SettingsAppDefinition } from './SettingsApp/definition';
import { IntegrationsAppDefinition } from './IntegrationsApp/definition';
import { IntegrationsHubAppDefinition } from './IntegrationsHubApp/definition';
import { APIConnectionAppDefinition } from './APIConnectionApp/definition';
import { AppBuilderAppDefinition } from './AppBuilderApp/definition';
import { DashboardAppDefinition } from './DashboardApp/definition';
import { RulesAppV2Definition } from './RulesApp_v2/definition';
import { ContactsAppDefinition } from './ContactsApp/definition';
import { LegalCodeViewerAppDefinition } from './LegalCodeViewerApp/definition';
import { TerminalAppDefinition } from './TerminalApp/definition';
import { IngestWizardDefinition } from './IngestWizard/definition';
import { SequenceBuilderAppDefinition } from './SequenceBuilderApp/definition';
import { SocialStudioAppDefinition } from './SocialStudioApp/definition';

// ── Games ────────────────────────────────────────────────────────────────
import { PongAppDefinition } from './PongApp/definition';
import { RapidsAppDefinition } from './RapidsApp/definition';

// ── Physician widgets ──────────────────────────────────────────────────────
import crclDefinition from './widgets/physicians/CrclApp/definition';
import denialDefinition from './widgets/physicians/DenialApp/definition';
import emDefinition from './widgets/physicians/EmApp/definition';
import icd10Definition from './widgets/physicians/Icd10App/definition';
import icd10xDefinition from './widgets/physicians/Icd10xApp/definition';
import interactionDefinition from './widgets/physicians/InteractionApp/definition';
import medicationTableDefinition from './widgets/physicians/MedicationTableApp/definition';

/**
 * All known app definitions keyed by appId.
 * Later entries win on duplicate appId — there should be none after the
 * canonical merge. The only intentional alias is 'drive' (DriveApp and
 * RiverDriveApp are different apps; entityBrowser2 is the Drive sidebar app).
 */
const ALL_DEFINITIONS: AppDefinition[] = [
  // Core productivity
  DocsAppDefinition,
  TasksAppDefinition,
  // NotesAppV2Definition must precede NotesMarkdownAppDefinition — both declare
  // entityTypes: ['Note'], and iconService.getAppForEntityType() returns the
  // first match, so ordering decides which app's icon/color wins for Note entities.
  NotesAppV2Definition,
  NotesMarkdownAppDefinition,
  MarkdownAppV2Definition,
  SheetsAppV2Definition,
  PDFiumAppDefinition,
  PasswordVaultAppDefinition,
  BankAppDefinition,
  BookkeeperAppDefinition,
  PageAppV2Definition,

  // Communication
  ChatAppDefinition,
  MailAppDefinition,
  MailOverviewAppDefinition,
  NotificationsAppDefinition,
  InboxAppDefinition,
  PromptsAppV2Definition,
  PromptLibraryAppDefinition,

  // Browsing & files
  BrowserAppDefinition,
  RiverDriveAppDefinition,
  DriveAppDefinition,
  GoogleDriveFileAppDefinition,
  LocalFileAppDefinition,
  FileViewerAppDefinition,
  FileEditorAppDefinition,
  ImageStudioAppDefinition,
  MediaViewerAppDefinition,
  WebDocumentAppDefinition,

  // Content creation
  CanvasAppDefinition,
  DiagramAppDefinition,
  ChartAppDefinition,
  SchemaEntityAppDefinition,
  VideoStudioAppDefinition,
  MediaGenAppDefinition,
  NeurvanaAppDefinition,
  WritersDashboardAppDefinition,
  BookFormatterAppDefinition,
  ManuscriptReviewReportAppDefinition,
  ListenAppDefinition,

  // AI & agents
  AutomationsAppDefinition,
  MemoryAppV2Definition,
  SkillsAppV2Definition,
  SkillWizardAppDefinition,
  transcriptAppDefinition,
  EntityGraphAppDefinition,
  DatabaseAppDefinition,

  // Organisation & system
  StoreAppDefinition,
  SettingsAppDefinition,
  IntegrationsAppDefinition,
  IntegrationsHubAppDefinition,
  APIConnectionAppDefinition,
  AppBuilderAppDefinition,
  DashboardAppDefinition,
  RulesAppV2Definition,
  ContactsAppDefinition,
  LegalCodeViewerAppDefinition,
  TerminalAppDefinition,
  IngestWizardDefinition,
  SequenceBuilderAppDefinition,
  SocialStudioAppDefinition,

  // Games
  PongAppDefinition,
  RapidsAppDefinition,

  // Physician widgets
  crclDefinition,
  denialDefinition,
  emDefinition,
  icd10Definition,
  icd10xDefinition,
  interactionDefinition,
  medicationTableDefinition,
];

export const APP_DEFINITIONS_BY_ID: ReadonlyMap<string, AppDefinition> = new Map(
  ALL_DEFINITIONS.filter(d => d.appId).map(d => [d.appId, d])
);

/**
 * Whether an app should be listed in discovery surfaces (Dock, Dock
 * Preferences, SlimCreatePalette, CreateMenu, AppGalleryModal, Store,
 * GlobalEntitySearch). Reads the `hidden` flag from the app's own
 * definition.ts (the manifest) — defaults to visible when the app has no
 * definition or omits the flag. This does not affect the app's window,
 * entity types, or backend routes, which remain fully functional.
 *
 * Also hides `experimental` apps from sessions that can't use experimental
 * features (non-platform-admins, or admins who toggled the feature off) —
 * see `canUseExperimentalFeatures()`.
 */
export function isAppHidden(appId: string): boolean {
  const def = APP_DEFINITIONS_BY_ID.get(appId);
  if (!def) return false;
  if (def.hidden === true) return true;
  if (def.experimental === true && !canUseExperimentalFeatures()) return true;
  return false;
}

/**
 * All appIds with a definition.ts that are currently visible — i.e. not
 * `isAppHidden()`. A function (not a cached Set) so it reflects the live
 * admin/experimental-toggle state rather than whatever was true at module load.
 */
export function getVisibleAppIds(): ReadonlySet<string> {
  return new Set(
    ALL_DEFINITIONS.filter(d => d.appId && !isAppHidden(d.appId)).map(d => d.appId)
  );
}
